import * as Sentry from "@sentry/react";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { App } from "@capacitor/app";
import { PushNotifications } from "@capacitor/push-notifications";
import { Preferences } from "@capacitor/preferences";
import { apiRequest } from "@/lib/queryClient";
import { isNativeApp, nativePlatform } from "./platform";

// Push registration for the native shell.
//
// Two-step model:
//  1. On app launch (launch screen mount) we request permission — the native
//    dialog is the loudest "real app" signal an App Review pass sees — and
//    register with APNs/FCM. The OS hands back a device token which we hold
//    in Preferences; it is NOT yet bound to anyone.
//  2. Once the buyer is inside a vendor with a live customer_session
//    (CustomerEntry), bindPushTokenToVendor() POSTs the held token to that
//    org's /customer/push/register, binding it server-side to the session's
//    (organization, account). Each vendor gets its own binding — a buyer
//    signed in to two distributors gets both distributors' reminders — and
//    signing out of a vendor removes only that vendor's binding.
//
// Ordering caveat that shapes the code below: PushNotifications.register()
// resolves as soon as the OS request is dispatched, BEFORE the token arrives.
// The token comes later through the "registration" event, which on iOS only
// fires if AppDelegate forwards the UIKit callback to the plugin. So a bind can
// legitimately run before there is anything to bind (fast sign-in on first
// launch) — that case is deferred, not dropped — and a broken native wiring
// produces NO event at all, which the watchdog in initPushOnLaunch reports.
// Every failure on this path goes to Sentry: the WebView console on a buyer's
// phone is not a place anyone looks.

const TOKEN_KEY = "fo_push_token";
// { [orgSlug]: `${accountId}:${token}` } — one entry per vendor bound on this
// device. It used to be a single `${orgSlug}:${token}` string, which was fine
// only while the server kept one binding per token.
const BINDINGS_KEY = "fo_push_bindings";
const LEGACY_BOUND_KEY = "fo_push_bound";
const REGISTRATION_WATCHDOG_MS = 30_000;

let listenersInstalled = false;
let registrationSeen = false; // did the OS deliver a token this launch?
interface BindTarget { orgSlug: string; accountId: number }
let pendingBind: BindTarget | null = null; // a bind ran before the token arrived
let lastBound: BindTarget | null = null; // re-bind target on app resume

type Bindings = Record<string, string>;

// Android only: register() CRASHES the app (not a rejected promise) when the
// build has no google-services.json — the plugin calls Firebase unguarded and
// Capacitor rethrows. The in-repo PushSupport plugin
// (android/app/src/main/java/io/freshorder/app/PushSupportPlugin.java) reports
// whether Firebase is configured. A build without the plugin is treated as
// unconfigured: skipping push is recoverable, a crash on launch is not.
interface PushSupportPlugin {
  isConfigured(): Promise<{ configured: boolean }>;
}
const PushSupport = registerPlugin<PushSupportPlugin>("PushSupport");

async function canRegisterForPush(): Promise<boolean> {
  if (nativePlatform() !== "android") return true;
  if (!Capacitor.isPluginAvailable("PushSupport")) return false;
  const { configured } = await PushSupport.isConfigured();
  return configured;
}

async function readBindings(): Promise<Bindings> {
  const { value } = await Preferences.get({ key: BINDINGS_KEY });
  if (!value) {
    // Drop the single-slot memo so it can never suppress a bind again.
    await Preferences.remove({ key: LEGACY_BOUND_KEY });
    return {};
  }
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

async function writeBindings(bindings: Bindings): Promise<void> {
  await Preferences.set({ key: BINDINGS_KEY, value: JSON.stringify(bindings) });
}

async function installListeners(): Promise<void> {
  if (listenersInstalled) return;
  listenersInstalled = true;

  await PushNotifications.addListener("registration", async (token) => {
    registrationSeen = true;
    try {
      await Preferences.set({ key: TOKEN_KEY, value: token.value });
    } catch (error) {
      // Nothing downstream can work without the stored token, and "try again
      // next launch" would hit the same failing write.
      console.warn("Push token could not be stored:", error);
      Sentry.captureException(error, { tags: { category: "push", flow: "store_token" } });
      return;
    }
    // A bind that ran before this token existed is waiting on it.
    if (pendingBind) {
      const target = pendingBind;
      pendingBind = null;
      void bindPushTokenToVendor(target.orgSlug, target.accountId);
    }
  });

  await PushNotifications.addListener("registrationError", (err) => {
    // An OS-level failure forwarded by AppDelegate (didFailToRegister…). Note
    // a MISSING forwarder produces no event of either kind; see the watchdog.
    console.error("Push registration error:", JSON.stringify(err));
    Sentry.captureMessage("Push registration error", {
      level: "warning",
      tags: { category: "push", flow: "register" },
      extra: { error: err },
    });
  });

  // APNs tokens rotate on device restore / OS update, and the server retires
  // the old one on BadDeviceToken. Re-bind on resume — cheap, because bind
  // skips the POST when the (vendor, token) pair is unchanged.
  await App.addListener("appStateChange", ({ isActive }) => {
    if (isActive && lastBound) void bindPushTokenToVendor(lastBound.orgSlug, lastBound.accountId);
  });
}

/**
 * Request permission + register with the OS push service. Call once per app
 * launch from the native launch screen. Safe no-op on web.
 */
export async function initPushOnLaunch(): Promise<void> {
  if (!isNativeApp()) return;
  try {
    if (!(await canRegisterForPush())) {
      // Not worth a Sentry event: release builds refuse to build without
      // Firebase (verifyPushConfig in android/app/build.gradle), so this is a
      // local debug build.
      console.warn("Push disabled: this Android build has no Firebase config");
      return;
    }
    await installListeners();
    let status = await PushNotifications.checkPermissions();
    if (status.receive === "prompt" || status.receive === "prompt-with-rationale") {
      status = await PushNotifications.requestPermissions();
    }
    if (status.receive !== "granted") return;

    registrationSeen = false;
    await PushNotifications.register();

    // register() resolving proves nothing. If no "registration" event follows,
    // the native wiring is broken — which is exactly how AppDelegate's missing
    // forwarders kept device_push_tokens empty for two months with no signal.
    setTimeout(() => {
      if (registrationSeen || !navigator.onLine) return;
      console.error("Push: no registration event within 30s of register()");
      Sentry.captureMessage("Push registration event never fired", {
        level: "warning",
        tags: { category: "push", flow: "register_watchdog" },
      });
    }, REGISTRATION_WATCHDOG_MS);
  } catch (error) {
    console.warn("Push init failed:", error);
    Sentry.captureException(error, { tags: { category: "push", flow: "init" } });
  }
}

/**
 * Bind the held device token to the vendor the buyer is currently signed into.
 * Requires a live customer_session (cookie) for the org — the server resolves
 * the session and binds the token to ITS account, never to client-supplied ids.
 * Idempotent: the same (vendor, account, token) triple is POSTed once, then
 * skipped. accountId is only part of the memo key — switching sub-accounts in
 * a group is a new session on a different account and must bind again.
 */
export async function bindPushTokenToVendor(orgSlug: string, accountId: number): Promise<void> {
  if (!isNativeApp()) return;
  const platform = nativePlatform();
  if (!platform) return;
  try {
    const { value: token } = await Preferences.get({ key: TOKEN_KEY });
    if (!token) {
      // Usually the token simply hasn't arrived yet; the registration listener
      // binds when it does. If it never does, the watchdog reports why.
      pendingBind = { orgSlug, accountId };
      console.warn("Push bind deferred: no device token stored yet");
      return;
    }
    lastBound = { orgSlug, accountId };
    const memo = `${accountId}:${token}`;
    const bindings = await readBindings();
    if (bindings[orgSlug] === memo) return;
    await apiRequest("POST", "/api/customer/push/register", { token, platform });
    // Re-read: another vendor's bind may have written while the POST was out.
    await writeBindings({ ...(await readBindings()), [orgSlug]: memo });
  } catch (error) {
    // Non-fatal: reminders simply won't push to this device yet.
    console.warn("Push bind failed:", error);
    Sentry.captureException(error, { tags: { category: "push", flow: "bind" } });
  }
}

/**
 * Remove this device's binding for the vendor being signed out of, BEFORE the
 * session is destroyed (unregister is authorized by that session). Without it
 * a signed-out account keeps pushing to the phone — and, now that one token
 * carries a binding per vendor/account, so would every account that ever
 * signed in on a shared device. Best effort: never blocks the sign-out.
 */
export async function unbindPushTokenFromVendor(orgSlug: string): Promise<void> {
  if (!isNativeApp()) return;
  try {
    if (lastBound?.orgSlug === orgSlug) lastBound = null;
    if (pendingBind?.orgSlug === orgSlug) pendingBind = null;
    const { value: token } = await Preferences.get({ key: TOKEN_KEY });
    if (!token) return;
    const bindings = await readBindings();
    if (orgSlug in bindings) {
      delete bindings[orgSlug];
      await writeBindings(bindings);
    }
    await apiRequest("POST", "/api/customer/push/unregister", { token });
  } catch (error) {
    console.warn("Push unbind failed:", error);
    Sentry.captureException(error, { tags: { category: "push", flow: "unbind" } });
  }
}
